import { useDate } from '@/composables/useDate.ts'
import { useHttpClient } from '@/composables/useHttpClient'
import { useLocalCache } from '@/composables/useLocalCache'
import { useLogError } from '@/composables/useLogError'
import { usePreprocessCube } from '@/composables/usePreprocessCube.ts'
import { CUBE_ORDER, EFRIEND_OF_THE_MONTH_URL, VAT_FACTOR, type CubeOrderValue } from '@/constants.ts'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCubeStore } from '@/stores/useCubeStore'
import type { CubeMapData, GetCubeListByCoordinatesResponse } from '@/types/CubeDto'
import { resolveSpecialPriceState, type SpecialPriceData } from '@/util/SpecialPriceResolver'
import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

export type MapStore = ReturnType<typeof useMapStore>

export const useMapStore = defineStore('map', () => {
  const { logError } = useLogError()
  const { backendApi, strapiApi, proxyApi } = useHttpClient()
  const { moment } = useDate()
  const { preprocessCube } = usePreprocessCube()

  // --- State ---

  const globalCubeList = ref<CubeMapData[]>([])
  const tourCubeList = ref<CubeMapData[]>([])
  const globalProducerPrices = ref<Record<string, any>>({})
  const producerPricesLoading = ref(false)
  const producerPricesLoadedFresh = ref(false)
  const producerPricesUpdating = ref(false)
  const eFriendsOfTheMonth = ref<string[]>([])
  const cubeOrder = ref<CubeOrderValue>(CUBE_ORDER.SHORTEST_DISTANCE_FIRST!)

  const defaultZoom = ref(16)
  const defaultZoomTour = ref(8)
  const coordinateConstraints = reactive({
    latMin: 46,
    latMax: 50,
    lonMin: 9.5,
    lonMax: 17.5,
  })

  // --- Getters ---

  function getCubeByName(cubeName: string, source: string = 'global'): CubeMapData | undefined {
    const cubeList = source === 'tour' //
      ? tourCubeList.value
      : globalCubeList.value
    return cubeList.find((cube) => cube.cubeName === cubeName)
  }

  function getProducerPrices(cubeName: string, options: { freshOnly?: boolean } = {}): any {
    if (options.freshOnly && !producerPricesLoadedFresh.value) {
      return undefined
    }
    return globalProducerPrices.value[cubeName]
  }

  function ensureProducerPriceEntry(prices: Record<string, any>, cubeName: string) {
    if (!prices[cubeName]) { prices[cubeName] = {} }
    if (!prices[cubeName].basePrices) { prices[cubeName].basePrices = {} }
    if (!prices[cubeName].specialPrices) { prices[cubeName].specialPrices = {} }
    return prices[cubeName]
  }

  function assignProducerPrice(target: SpecialPriceData, row: any) {
    const price = parseFloat(row.preis) * VAT_FACTOR
    if (row.zeittyp === 'aktuell') {
      target.currentPrice = price
      if (row.gueltig_bis) {
        target.currentPriceValidUntil = moment(row.gueltig_bis, 'X').toDate()
      }
    } else if (row.zeittyp === 'next') {
      target.nextPrice = price
      target.dateNextPrice = moment(row.gueltig_ab, 'X').toDate()
      if (row.gueltig_bis) {
        target.currentPriceValidUntil = moment(row.gueltig_bis, 'X').toDate()
      }
    }
  }

  function applyResolvedProducerPrices(prices: Record<string, any>) {
    Object.values(prices).forEach((entry: any) => {
      const resolved = resolveSpecialPriceState(entry.basePrices, entry.specialPrices)
      entry.currentPrice = resolved.currentPrice
      entry.nextPrice = resolved.nextPrice ?? false
      entry.dateNextPrice = resolved.nextDate || false
      entry.priceType = resolved.currentSource === 'special' ? 'sonder' : 'basis'
      entry.currentPriceValidUntil = entry.specialPrices?.currentPriceValidUntil
    })
  }

  /**
   * Accepts either a cube object (with .cubeName) or a bare cubeName string.
   */
  function isEfriendOfTheMonth(cubeOrCubeName?: CubeMapData | string): boolean {
    if (cubeOrCubeName == undefined) {
      return false;
    }

    const cubeName =
      typeof cubeOrCubeName === 'string'
        ? cubeOrCubeName
        : cubeOrCubeName?.cubeName

    return eFriendsOfTheMonth.value.includes(cubeName)
  }

  function cubeIconUrl(cube: Partial<CubeMapData> | null = null, imgType: string = 'png'): string | undefined {
    if (!cube || typeof cube !== 'object') {
      return undefined
    }

    if (isEfriendOfTheMonth(cube.cubeName)) {
      return EFRIEND_OF_THE_MONTH_URL
    }

    if (cube.icon) {
      if (cube.icon.isCustom) { return cube.icon.fileName }
      return `${cube.icon.urlBase}${cube.icon.fileName}.${imgType}`
    }

    return undefined;
  }

  /**
   * @param ignoreUndefinedProperties if set to false, properties of the patch object with values undefined are applied to the object to patch.
   */
  function patchGlobalCube(cubeName: string, patch: Partial<CubeMapData>, ignoreUndefinedProperties = true) {
    const index = globalCubeList.value.findIndex((c: any) => c.cubeName === cubeName)

    if (index >= 0) {
      const cleanPath = ignoreUndefinedProperties
        ? Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined))
        : patch

      globalCubeList.value[index] = { ...globalCubeList.value[index], ...cleanPath } as CubeMapData
    }
  }

  // --- Setters ---

  function setCubeList(listName: string, cubes: CubeMapData[]) {
    if (listName === 'tour') {
      tourCubeList.value = cubes
    } else {
      globalCubeList.value = cubes
    }
  }

  function setProducerPrices(prices: Record<string, any>) {
    globalProducerPrices.value = { ...prices }
  }

  // --- Actions ---

  async function loadGlobalCubeList() {
    const cube = useCubeStore()
    try {
      const result: GetCubeListByCoordinatesResponse = await backendApi.get('/app/getCubeListByCoordinates', {
        params: { ownCubeName: cube.name, ...coordinateConstraints },
      })

      if (!result?.data?.bSuccess) {
        throw new Error('Global cubes list request returned unsuccessful.')
      }

      // Process cubes and set Pinia state directly.
      const homeCoordinates = { lat: cube.latitude, lng: cube.longitude }

      globalCubeList.value = Object
        .keys(result.data.data)
        .map((cubeName) => preprocessCube(result.data.data[cubeName], homeCoordinates))
        .filter(cube => !!cube)

      loadProducerPrices() // fire-and-forget — cache shown immediately, network runs in background
    } catch (err) {
      logError('Requesting global cubes list failed: ' + err)
    }
  }

  async function loadProducerPrices() {
    const auth = useAuthStore()
    const cube = useCubeStore()
    const cache = useLocalCache<Record<string, any>>(`efriends:producerPrices:${cube.name}`)
    const hadFreshPrices = producerPricesLoadedFresh.value
    producerPricesLoading.value = !hadFreshPrices
    producerPricesUpdating.value = hadFreshPrices

    // Serve stale data immediately so the UI is not blocked
    const stale = cache.read()
    if (stale && !hadFreshPrices) {
      setProducerPrices(stale)
      // we agreed to load fresh prices in the background even if cache is stale, so we show the loading indicator in that case — but if cache is fresh, we can skip loading fresh data at all
      // if (cache.isFresh()) { return }
      producerPricesUpdating.value = true // only shown when stale data is already visible
    }
    try {
      const result = await proxyApi.get('/artikelproduzentenpreise', {
        params: {
          access_token: auth.backendAccessToken,
          fields: 'get_konto,get_artikel',
          cubevertrag: cube.name,
        },
      })

      if (!result?.data?.list) {
        throw new Error('Get producer prices for map request failed.')
      }

      const prices: Record<string, any> = {}
      const errors: any[] = []
      result.data.list.forEach((row: any) => {
        const cubeName = row.get_artikel.artikelnr
        if (Object.prototype.hasOwnProperty.call(row, 'preis')) {
          const entry = ensureProducerPriceEntry(prices, cubeName)
          const target = row.preistyp === 'sonder' ? entry.specialPrices : entry.basePrices
          assignProducerPrice(target, row)
          if (
            Object.prototype.hasOwnProperty.call(row, 'netzkostenersparnis_netto') &&
            parseInt(row.included_netzkostenersparnis) !== 0
          ) {
            entry.gridFeeDiscountApplies = true
            entry.gridFeeDiscountPriceBrutto = Math.abs(row.netzkostenersparnis_brutto * 100)
          }
        } else {
          errors.push(row)
        }
      })
      applyResolvedProducerPrices(prices)

      // Only update reactive state if prices actually changed — avoids chart re-render flicker
      if (JSON.stringify(prices) !== JSON.stringify(globalProducerPrices.value)) {
        setProducerPrices(prices)
      }
      producerPricesLoadedFresh.value = true
      cache.write(prices)
      if (errors.length > 0) { throw new Error(`price is falsy: ${JSON.stringify(errors)}`) }
    } catch (err) {
      logError('Error getting producer prices for map: ' + err)
    } finally {
      producerPricesLoading.value = false
      producerPricesUpdating.value = false
    }
  }

  async function getEfriendsOfTheMonth() {
    const now = new Date()
    const year = now.getFullYear()
    const month = (now.getMonth() + 1).toString().padStart(2, '0')
    try {
      const response = await strapiApi.get('/api/ef-otms', {
        params: { 'filters[Month][$eq]': `${year}-${month}-01` },
      })

      if (response?.status === 200) {
        const data = response.data?.data ?? []
        eFriendsOfTheMonth.value = data.map((d: any) => d.cubename)
      }
    } catch (err) {
      logError('Error getting eFriends of the month: ' + err)
    }
  }

  // Called by useAuthStore.clearAuth() on logout.
  function reset() {
    const cube = useCubeStore()
    useLocalCache(`efriends:producerPrices:${cube.name}`).clear()
    globalCubeList.value = []
    tourCubeList.value = []
    globalProducerPrices.value = {}
    producerPricesLoading.value = false
    producerPricesLoadedFresh.value = false
    producerPricesUpdating.value = false
    eFriendsOfTheMonth.value = []
  }

  return {
    // State
    cubeOrder,
    globalCubeList,
    tourCubeList,
    globalProducerPrices,
    producerPricesLoading,
    producerPricesLoadedFresh,
    producerPricesUpdating,
    eFriendsOfTheMonth,
    defaultZoom,
    defaultZoomTour,
    coordinateConstraints,
    // Getters (functions)
    getCubeByName,
    getProducerPrices,
    isEfriendOfTheMonth,
    cubeIconUrl,
    // Setters
    patchGlobalCube,
    setCubeList,
    // Actions
    loadGlobalCubeList,
    loadProducerPrices,
    getEfriendsOfTheMonth,
    reset,
  }
})
